import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { SupplierForm, emptySupplier, toSupplierForm } from "./SupplierForm";
import { SupplierDetail } from "./SupplierDetail";

const CATEGORY_LABEL = {
  distributor: "Distributor", stockist: "Stockist", wholesaler: "Wholesaler",
  manufacturer: "Manufacturer", importer: "Importer", c_and_f: "C&F agent",
  local_vendor: "Local vendor", other: "Other",
};

const Pill = ({ active }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
    active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
    {active ? "Active" : "Inactive"}
  </span>
);

export default function Suppliers({ embedded = false }) {
  const { can, user } = useAuth();
  const isAdmin = ["business_admin", "pharmacy_admin", "platform_owner"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySupplier());
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { supplier, mode: 'delete' | 'deactivate' }
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/pharmacy/suppliers", {
        params: { q: q || undefined, status: status || undefined },
      });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [q, status]);

  useEffect(() => {
    if (!can("pharmacy.inventory.view")) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, can]);

  if (!can("pharmacy.inventory.view")) return <NoAccess what="suppliers" />;

  const submit = async (e) => {
    e.preventDefault(); setFormError(""); setBusy(true);
    const body = {
      ...form,
      payment_terms_days: Number(form.payment_terms_days || 0),
      credit_limit: Number(form.credit_limit || 0),
    };
    try {
      const { data } = editing
        ? await api.patch(`/pharmacy/suppliers/${editing}`, body)
        : await api.post("/pharmacy/suppliers", body);
      toast.success(editing ? "Supplier updated." : "Supplier added.");
      (data.warnings || []).forEach((w) => toast.warning(w));
      setOpen(false); setEditing(null); setForm(emptySupplier()); load();
    } catch (err) { setFormError(apiError(err)); } finally { setBusy(false); }
  };

  const act = async () => {
    const { supplier, mode } = confirm;
    try {
      if (mode === "delete") {
        await api.delete(`/pharmacy/suppliers/${supplier.supplier_id}`, { params: { reason } });
        toast.success("Supplier deleted permanently.");
      } else {
        await api.post(`/pharmacy/suppliers/${supplier.supplier_id}/status`,
          { status: supplier.status === "active" ? "inactive" : "active", reason });
        toast.success(supplier.status === "active" ? "Supplier deactivated." : "Supplier reactivated.");
      }
      setConfirm(null); setReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="suppliers-page">
      {embedded ? (
        can("pharmacy.suppliers.manage") && (
          <div className="mb-6">
            <button onClick={() => { setEditing(null); setForm(emptySupplier()); setOpen(true); }}
              className={primaryBtnCls} data-testid="add-supplier-btn">Add supplier</button>
          </div>
        )
      ) : (
        <PageHeader overline="Pharmacy" title="Suppliers"
          subtitle="Full supplier profiles with GST, drug licence, bank details, linked products, purchase history and ledger."
          actions={can("pharmacy.suppliers.manage") && (
            <button onClick={() => { setEditing(null); setForm(emptySupplier()); setOpen(true); }}
              className={primaryBtnCls} data-testid="add-supplier-btn">Add supplier</button>
          )} />
      )}

      <div className="mb-6 flex flex-wrap gap-3">
        <input placeholder="Search name, contact, mobile, GSTIN or city" value={q}
          data-testid="supplier-search-input" onChange={(e) => setQ(e.target.value)}
          className={`${inputCls} sm:!w-96`} />
        <select className={`${inputCls} sm:!w-44`} value={status} data-testid="supplier-status-filter"
          onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="suppliers-table"
          columns={["Supplier", "Contact", "Mobile / Email", "GSTIN / Drug licence", "Terms", "Status", ""]}
          rows={items}
          empty={<EmptyState title="No suppliers yet" subtitle="Add a supplier before recording purchases." testid="suppliers-empty" />}
          renderRow={(s) => (
            <tr key={s.supplier_id} data-testid={`supplier-row-${s.supplier_id}`}>
              <Td className="font-medium text-slate-900">
                {s.name}
                <span className="block text-xs text-slate-400">
                  {CATEGORY_LABEL[s.category] || "Supplier"}{s.city ? ` · ${s.city}` : ""}
                </span>
              </Td>
              <Td className="text-slate-600">{s.contact_person || "—"}</Td>
              <Td className="text-slate-600">
                {s.phone || "—"}<span className="block text-xs text-slate-400">{s.email || "—"}</span>
              </Td>
              <Td className="text-xs text-slate-500">
                {s.gstin || "—"}<span className="block">{s.drug_license_no || "—"}</span>
              </Td>
              <Td className="text-slate-600">{s.payment_terms_days ? `${s.payment_terms_days} days` : "—"}</Td>
              <Td><Pill active={(s.status || "active") === "active"} /></Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setDetailId(s.supplier_id)} className={ghostBtnCls}
                    data-testid={`view-supplier-${s.supplier_id}`}>View</button>
                  {can("pharmacy.suppliers.manage") && (
                    <button onClick={() => { setEditing(s.supplier_id); setForm(toSupplierForm(s)); setFormError(""); setOpen(true); }}
                      className={ghostBtnCls} data-testid={`edit-supplier-${s.supplier_id}`}>Edit</button>
                  )}
                  {isAdmin && can("pharmacy.suppliers.delete") && (
                    <>
                      <button onClick={() => { setConfirm({ supplier: s, mode: "status" }); setReason(""); }}
                        className={ghostBtnCls} data-testid={`toggle-supplier-${s.supplier_id}`}>
                        {(s.status || "active") === "active" ? "Deactivate" : "Activate"}
                      </button>
                      {s.can_delete !== false ? (
                        <button onClick={() => { setConfirm({ supplier: s, mode: "delete" }); setReason(""); }}
                          data-testid={`delete-supplier-${s.supplier_id}`}
                          className="inline-flex items-center rounded-full border border-red-200 bg-white px-5 py-2 text-sm font-medium text-red-600 hover:border-red-500">
                          Delete
                        </button>
                      ) : (
                        <span className="self-center text-xs text-slate-400" data-testid={`delete-blocked-${s.supplier_id}`}>
                          Has transactions — deactivate only
                        </span>
                      )}
                    </>
                  )}
                </div>
              </Td>
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} wide
        title={editing ? "Edit supplier" : "Add supplier"}
        subtitle="GSTIN and supplier name must be unique within your business."
        testid="supplier-modal">
        <SupplierForm form={form} setForm={setForm} onSubmit={submit} busy={busy}
          error={formError} editing={Boolean(editing)} />
      </Modal>

      <SupplierDetail supplierId={detailId} onClose={() => setDetailId(null)}
        onChanged={load}
        onEdit={(s) => { setDetailId(null); setEditing(s.supplier_id); setForm(toSupplierForm(s)); setOpen(true); }} />

      <Modal open={Boolean(confirm)} onClose={() => setConfirm(null)}
        title={confirm?.mode === "delete" ? "Delete supplier permanently"
          : (confirm?.supplier?.status || "active") === "active" ? "Deactivate supplier" : "Reactivate supplier"}
        subtitle={confirm?.supplier?.name} testid="supplier-confirm-modal">
        {confirm && (
          <form onSubmit={(e) => { e.preventDefault(); act(); }} className="space-y-5">
            <p className="text-sm text-slate-600">
              {confirm.mode === "delete"
                ? "Permanent deletion is only allowed when the supplier has never been used in a purchase, batch, payment or return. Otherwise deactivate it instead."
                : (confirm.supplier.status || "active") === "active"
                  ? "A deactivated supplier stays visible in all historical records but will no longer appear when recording a new purchase."
                  : "The supplier will appear again in new purchase entry."}
            </p>
            {!(confirm.mode === "status" && confirm.supplier.status !== "active") && (
              <Field label="Reason * (recorded in the audit log)">
                <input required minLength={5} className={inputCls} value={reason}
                  data-testid="supplier-reason-input" onChange={(e) => setReason(e.target.value)} />
              </Field>
            )}
            <button className={confirm.mode === "delete"
              ? "inline-flex items-center rounded-full bg-red-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700"
              : primaryBtnCls} data-testid="supplier-confirm-btn">
              {confirm.mode === "delete" ? "Delete supplier" :
                (confirm.supplier.status || "active") === "active" ? "Deactivate" : "Activate"}
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
}
